import type { PaginationState, SortingState } from '@tanstack/react-table'
import { useCallback, useContext, useMemo, useState } from 'react'

import type { ColumnFilters } from '@/components/filters/column-filter'
import { DataTable, type DataTableProps } from '@/components/data-table/DataTable'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { buildReportColumns, renderCell } from '@/features/report/columns'
import type { TableLayout } from '@/hooks/use-table-preferences'
import { agingBucketKey, agingBuckets, agingTotals, AGING_MODES, type AgingMode } from '@/lib/aging'
import { toNumber, type AgingWidget } from '@/lib/cube-report'
import { todayAsDateInput } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { ReportRow } from '@/services/cube/report'

import { field } from './helpers'
import { InTabs } from './shared'
import type { WidgetStatus } from './types'

/** Canlı kontroller: mod adres çubuğunda, sütun filtresi ve düzen listeyle aynı desende. */
export interface AgingControls {
  mode: AgingMode
  onModeChange: (mode: AgingMode) => void
  layout: TableLayout
  onLayoutChange: (layout: TableLayout) => void
  onLayoutReset: () => void
  columnFilters: ColumnFilters
  onColumnFiltersChange: (filters: ColumnFilters) => void
  filterValues: DataTableProps<ReportRow>['filterValues']
}

/** Hesaplanan hücreler satıra bu önekle eklenir; Cube üye adlarıyla (`View.uye`) çakışmaz. */
const BUCKET_PREFIX = 'aging:'
const TOTAL_COLUMN = `${BUCKET_PREFIX}total`
/** Toplam satırının etiketi; varsa satır üstte, kalın çizilir. */
const TOTAL_LABEL = 'aging#label'

const EMPTY_LAYOUT: TableLayout = { columnOrder: [], columnSizing: {}, columnVisibility: {} }
const noop = () => {}
const collator = new Intl.Collator('tr', { numeric: true })

const isBlank = (value: unknown) => value === null || value === undefined || value === ''

/**
 * Sütunun sıralama anahtarları, satır başına bir kez: dolu değerlerin hepsi sayıysa
 * (Cube sayı boyutunu metin döndürebilir, "1500.00") sayı; değilse farklı değerler bir
 * kez Türkçe sıralanıp sıra numarası. Karşılaştırma sırasında dönüşüm ve collator çağrısı
 * yapılmaz (binlerce satırda her tıklamada saniyelerce sürüyordu). Boşlar `null`.
 */
function sortKeys(rows: ReportRow[], id: string): (number | null)[] {
  const values = rows.map((row) => row[id])
  const numbers = values.map((value) => (isBlank(value) ? null : Number(value)))
  if (numbers.every((value) => value === null || Number.isFinite(value))) return numbers
  const distinct = [...new Set(values.filter((value) => !isBlank(value)).map(String))]
  const rank = new Map(distinct.sort(collator.compare).map((value, index) => [value, index]))
  return values.map((value) => (isBlank(value) ? null : rank.get(String(value))!))
}

function sortRows(rows: ReportRow[], sorting: SortingState): ReportRow[] {
  if (sorting.length === 0) return rows
  const columns = sorting.map(({ id, desc }) => ({ keys: sortKeys(rows, id), sign: desc ? -1 : 1 }))
  const order = rows.map((_, index) => index)
  order.sort((a, b) => {
    for (const { keys, sign } of columns) {
      const keyA = keys[a]!
      const keyB = keys[b]!
      // Boşlar yön ne olursa olsun sonda.
      if (keyA === null || keyB === null) {
        if (keyA !== keyB) return keyA === null ? 1 : -1
        continue
      }
      if (keyA !== keyB) return (keyA - keyB) * sign
    }
    return 0
  })
  return order.map((index) => rows[index]!)
}

/**
 * Saf çizim: süzülmüş tüm kayıtlar prop'tan gelir; dilimleme, sıralama ve sayfalama burada.
 * `controls` yoksa (önizleme) mod değişimi yalnız bileşen içinde kalır.
 */
export function AgingWidgetView({
  widget,
  data,
  status = {},
  controls,
}: {
  widget: AgingWidget
  data: ReportRow[] | undefined
  status?: WidgetStatus
  controls?: AgingControls
}) {
  const inTabs = useContext(InTabs)
  const [ownMode, setOwnMode] = useState<AgingMode>('day')
  const mode = controls?.mode ?? ownMode
  const setMode = controls?.onModeChange ?? setOwnMode
  const [sorting, setSorting] = useState<SortingState>(() =>
    widget.defaultSort.map((item) => ({ id: item.member, desc: item.desc })),
  )
  const [requested, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 })

  const amount = field(widget, widget.amount)
  const currencyMember = amount.currencyMember
  const prepared = useMemo(() => {
    const today = todayAsDateInput()
    const items = (data ?? []).map((row) => ({
      row,
      bucket: agingBucketKey(row[widget.date] as string | null, today, mode, widget.step),
    }))
    const buckets = agingBuckets(
      items.flatMap((item) => item.bucket ?? []),
      mode,
      widget.step,
    )
    const rows: ReportRow[] = items.map(({ row, bucket }) => ({
      ...row,
      ...(bucket !== undefined && { [`${BUCKET_PREFIX}${bucket}`]: row[widget.amount] }),
      [TOTAL_COLUMN]: row[widget.amount],
    }))
    const totals: ReportRow[] = agingTotals(items, widget.amount, currencyMember).map((entry) => ({
      ...(currencyMember && { [currencyMember]: entry.currency }),
      [TOTAL_LABEL]: messages.aging.totalLabel(entry.currency, entry.count),
      [TOTAL_COLUMN]: entry.sums.get('') ?? 0,
      ...Object.fromEntries(
        buckets.map((bucket) => [`${BUCKET_PREFIX}${bucket.key}`, entry.sums.get(bucket.key)]),
      ),
    }))
    return { buckets, rows, totals }
  }, [data, mode, widget, currencyMember])

  const columns = useMemo(() => {
    const info = buildReportColumns({
      dimensions: widget.dimensions,
      rowKey: widget.rowKey,
      sortable: new Set(widget.dimensions.map((item) => item.name)),
    }).map((column, index) => ({
      ...column,
      // Toplam satırında bilgi kolonları boş; etiket ilk kolonda.
      cell: (context: Parameters<Exclude<typeof column.cell, string | undefined>>[0]) => {
        const label = context.row.original[TOTAL_LABEL]
        if (label !== undefined) return index === 0 ? label : null
        return typeof column.cell === 'function' ? column.cell(context) : null
      },
    }))
    const amountColumn = (id: string, header: string) => ({
      id,
      header,
      accessorFn: (row: ReportRow) => row[id],
      enableSorting: true,
      // Kayıt tek dilimde; diğer dilimler "—" ile dolmasın, boş kalsın.
      cell: (context: { row: { original: ReportRow } }) => {
        const value = context.row.original[id]
        return value === undefined || value === null
          ? null
          : renderCell(amount, value, context.row.original)
      },

      meta: {
        title: header,
        exportValue: (row: ReportRow) => toNumber(row[id]) ?? '',
        exportNumberFormat: amount.numberFormat,
      },
    })
    return [
      ...info,
      ...prepared.buckets.map((bucket) =>
        amountColumn(`${BUCKET_PREFIX}${bucket.key}`, bucket.label),
      ),
      amountColumn(TOTAL_COLUMN, messages.aging.total),
    ]
  }, [widget, amount, prepared.buckets])

  const sorted = useMemo(() => sortRows(prepared.rows, sorting), [prepared.rows, sorting])
  // Filtre kayıtları azaltınca istenen sayfa sonun ötesinde kalabilir: son sayfaya çekilir.
  const lastPage = Math.max(Math.ceil(sorted.length / requested.pageSize) - 1, 0)
  const pagination = useMemo(
    () => ({ ...requested, pageIndex: Math.min(requested.pageIndex, lastPage) }),
    [requested, lastPage],
  )
  const page = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return [...prepared.totals, ...sorted.slice(start, start + pagination.pageSize)]
  }, [prepared.totals, sorted, pagination])
  const rowClassName = useCallback(
    (row: ReportRow) =>
      row[TOTAL_LABEL] !== undefined ? 'bg-muted/60 font-semibold hover:bg-muted/60' : undefined,
    [],
  )

  const modeToggle = (
    <ToggleGroup
      variant="outline"
      size="sm"
      aria-label={messages.aging.modeLabel}
      value={[mode]}
      onValueChange={([value]) => {
        if (value) {
          setMode(value as AgingMode)
          setPagination((current) => ({ ...current, pageIndex: 0 }))
        }
      }}
    >
      {AGING_MODES.map((item) => (
        <ToggleGroupItem key={item} value={item}>
          {messages.aging.modes[item]}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )

  const table = (
    <DataTable
      title={
        <span className="flex items-center gap-3">
          {!inTabs && widget.title}
          {modeToggle}
        </span>
      }
      tableId={`${widget.view.toLowerCase()}-aging`}
      columns={columns}
      data={status.isLoading ? [] : page}
      total={sorted.length}
      pagination={pagination}
      onPaginationChange={(updater) =>
        setPagination(typeof updater === 'function' ? updater(pagination) : updater)
      }

      sorting={sorting}
      onSortingChange={(updater) => {
        setSorting((current) => (typeof updater === 'function' ? updater(current) : updater))
        setPagination((current) => ({ ...current, pageIndex: 0 }))
      }}
      isLoading={status.isLoading}
      busy={status.busy}
      isError={status.isError}
      error={status.error}
      onRetry={status.onRetry}
      layout={controls?.layout ?? EMPTY_LAYOUT}
      onLayoutChange={controls?.onLayoutChange ?? noop}
      onLayoutReset={controls?.onLayoutReset ?? noop}
      columnFilters={controls?.columnFilters}
      onColumnFiltersChange={controls?.onColumnFiltersChange}
      filterValues={controls?.filterValues}
      rowClassName={rowClassName}
      exportData={sorted}
    />
  )
  return inTabs ? <div className="p-3">{table}</div> : table
}
